import { useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { formatDayMonth, toLocalIsoDate } from "@acto/taskpane/field-sales-2/utils/format";
import { daysOverdue, startOfToday } from "@acto/taskpane/field-sales-2/utils/todoDueLabels";
import { SquarePlus, TodosIcon } from "@acto/web-common/components";
import type { Todo } from "@acto/taskpane/core/domain";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import { BottomNavActionPill } from "@acto/taskpane/field-sales-2/components/patterns/BottomNav";
import { useOpenCreateTodo } from "@acto/taskpane/field-sales-2/components/patterns/CreateTodoSheet";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import {
  TodoLoadMoreFooter,
  TodoSection,
  TodoSectionError,
} from "@acto/taskpane/field-sales-2/components/patterns/TodoSection";
import { bucketShowsDividers } from "@acto/taskpane/field-sales-2/components/patterns/TodoBucketSection";
import { TodoListItem } from "@acto/taskpane/field-sales-2/components/patterns/TodoListItem";
import { TodoEditSheet } from "@acto/taskpane/field-sales-2/components/patterns/TodoEditSheet";
import {
  useCustomerTodosForPage,
  type CustomerTodoBucket,
} from "@acto/taskpane/field-sales-2/data-access/todos/useCustomerTodosForPage";
import { useUpdateTodos } from "@acto/taskpane/core/data-access/todos/useUpdateTodos";
import { AnalyticsEvents, useTrack, useViewTrackedOnce } from "@acto/taskpane/core/analytics";
import { todosToBuckets } from "@acto/taskpane/core/chat-envelope";
import { TODO_LIST_LIMIT } from "@acto/taskpane/field-sales-2/utils/chatScope";
import { useContributeCustomerDetailScope } from "@acto/taskpane/field-sales-2/utils/useContributeCustomerDetailScope";
import { useViewer } from "@acto/taskpane/core/contexts/ViewerContext";
import { useOptimisticTodoCompletion } from "./useOptimisticTodoCompletion";
import { useOptimisticTodoReassignment } from "./useOptimisticTodoReassignment";
import { TodoUndoToasts } from "./TodoUndoToasts";
import { mergeReassignedIntoOthers } from "./pendingReassignmentMerge";
import { styled } from "@pigment-css/react";
import { EmptyState, EmptySubtitle, EmptyTitle } from "@acto/taskpane/field-sales-2/components/primitives/EmptyState";

const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[48],
}));

export function TodosPage() {
  const t = useTranslations("fieldSalesTwo.todosList");
  const tNav = useTranslations("fieldSalesTwo.bottomNav");
  const { locale } = useLocale();
  const { id } = useParams<{ id: string }>();
  const openCreateTodo = useOpenCreateTodo();
  const { customer, isPending: isCustomerPending } = useOutletContext<CustomerOutletContext>();
  const {
    overdue,
    open,
    completed,
    assignedToOthers: others,
    isPending: isTodosPending,
    error,
  } = useCustomerTodosForPage(id);
  const { updateTodos } = useUpdateTodos();
  const track = useTrack();
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);

  useViewTrackedOnce(() => {
    if (!customer) return;
    track(AnalyticsEvents.TODOS_VIEWED, {
      source: "customer_details",
      customerId: customer.id,
      customerName: customer.name,
    });
  }, customer?.id ?? null);

  const {
    overdueShown,
    openShown,
    completedShown,
    isCompleting,
    handleComplete,
    handleUndo,
    pendingCount,
    isReverting,
    handleUncomplete,
    handleUndoRevert,
    revertPendingCount,
    flushPending: flushCompletionBatches,
  } = useOptimisticTodoCompletion(updateTodos, {
    overdue: overdue.todos,
    open: open.todos,
    completed: completed.todos,
  });

  const shownTodos = [...overdueShown, ...openShown, ...completedShown];
  const {
    assigneeFor,
    assigneeNameFor,
    handleReassign,
    handleUndo: handleReassignUndo,
    pendingCount: reassignPendingCount,
  } = useOptimisticTodoReassignment(updateTodos, shownTodos, flushCompletionBatches);

  const { isViewer } = useViewer();
  const merged = useMemo(
    () =>
      mergeReassignedIntoOthers(
        { overdue: overdueShown, open: openShown, completed: completedShown },
        others.todos,
        assigneeFor,
        isViewer,
      ),
    [overdueShown, openShown, completedShown, others.todos, assigneeFor, isViewer],
  );

  // Ambient context: the customer's rendered todo buckets (this page has no search/filter).
  const todosFragment = todosToBuckets(
    { overdue: merged.overdue, open: merged.open, completed: merged.completed },
    { limit: TODO_LIST_LIMIT },
  );
  useContributeCustomerDetailScope("todos", todosFragment);

  const totalCount = overdue.todos.length + open.todos.length + completed.todos.length + others.todos.length;
  // Buckets settle independently, so the skeleton has to cover all four.
  const isLoading = isCustomerPending || isTodosPending;
  const allBucketsFailed = [overdue, open, completed, others].every((bucket) => bucket.error);
  const allEmpty = !isLoading && !error && totalCount === 0;
  const today = startOfToday();

  const renderItem = (todo: Todo, variant: "overdue" | "open" | "completed") => {
    const kind = todo.isWorkflow ? "workflow" : todo.isNoteBased ? "recap" : "todo";
    const kindLabel = todo.isWorkflow ? t("meta.workflow") : todo.isNoteBased ? t("meta.recap") : t("meta.todo");
    const dueDateLabel = todo.dueDate ? formatDayMonth(toLocalIsoDate(todo.dueDate), locale) : undefined;
    const overdueLabel =
      variant === "overdue" && todo.dueDate
        ? t("meta.overdueDays", { count: daysOverdue(todo.dueDate, today) })
        : undefined;

    return (
      <TodoListItem
        key={todo.id}
        // Todos that are based on a workflow or note don't have a title, so those need the description
        title={todo.title?.length === 0 ? (todo.description ?? "") : todo.title}
        isCompleted={variant === "completed"}
        kind={kind}
        kindLabel={kindLabel}
        dueDateLabel={overdueLabel ? undefined : dueDateLabel}
        overdueLabel={overdueLabel}
        assigneeName={assigneeNameFor(todo)}
        onComplete={variant === "completed" ? undefined : () => handleComplete(todo)}
        onUncomplete={variant === "completed" ? () => handleUncomplete(todo) : undefined}
        onRowClick={variant === "completed" ? undefined : () => setEditingTodo(todo)}
        isCompleting={isCompleting(todo.id)}
        isReverting={isReverting(todo.id)}
        completeLabel={t("completeAriaLabel")}
        uncompleteLabel={t("uncompleteAriaLabel")}
      />
    );
  };

  const renderOthersItem = (todo: Todo) => {
    const kind = todo.isNoteBased ? "recap" : "todo";
    const kindLabel = todo.isNoteBased ? t("meta.recap") : t("meta.todo");
    const dueDateLabel = todo.dueDate ? formatDayMonth(toLocalIsoDate(todo.dueDate), locale) : undefined;

    return (
      <TodoListItem
        key={todo.id}
        title={todo.title?.length === 0 ? (todo.description ?? "") : todo.title}
        isCompleted={todo.isCompleted()}
        kind={kind}
        kindLabel={kindLabel}
        dueDateLabel={dueDateLabel}
        assigneeName={todo.assignee?.displayName ?? undefined}
        readOnly
      />
    );
  };

  const sectionFooter = (bucket: CustomerTodoBucket) => {
    if (bucket.error) {
      // Rows already on screen mean the next page failed, not the bucket.
      const message = bucket.todos.length > 0 ? t("loadMoreError") : t("loadError");
      return (
        <TodoSectionError
          message={message}
          retryLabel={t("retry")}
          onRetry={bucket.retry}
          isRetrying={bucket.isRetrying}
        />
      );
    }
    return bucket.hasNextPage ? (
      <TodoLoadMoreFooter label={t("loadMore")} isFetching={bucket.isFetching} onLoadMore={bucket.fetchNextPage} />
    ) : undefined;
  };

  const emptyState = allEmpty ? (
    <EmptyState>
      <TodosIcon aria-hidden="true" />
      <EmptyTitle>{t("empty")}</EmptyTitle>
      <EmptySubtitle>{t("emptySubtitle")}</EmptySubtitle>
    </EmptyState>
  ) : undefined;

  return (
    <>
      {id && (
        <ShellSlotContent slot="bottomNavAction" id="customerTodosCreate">
          <BottomNavActionPill onClick={openCreateTodo}>
            <SquarePlus width={18} height={18} aria-hidden="true" />
            {tNav("newTodo")}
          </BottomNavActionPill>
        </ShellSlotContent>
      )}
      <PageLayout
        hPadding="flush"
        isLoading={isLoading}
        isError={allBucketsFailed}
        isEmpty={(!customer && !isCustomerPending) || allEmpty}
        emptyState={emptyState}
      >
        <ContentWrapper>
          <TodoSection title={t("sections.overdue")} footer={sectionFooter(overdue)}>
            {merged.overdue.map((todo) => renderItem(todo, "overdue"))}
          </TodoSection>
          <TodoSection title={t("sections.open")} footer={sectionFooter(open)}>
            {merged.open.map((todo) => renderItem(todo, "open"))}
          </TodoSection>
          {(merged.others.length > 0 || others.hasNextPage || others.error) && (
            <TodoSection title={t("sections.assignedToOthers")} footer={sectionFooter(others)}>
              {merged.others.map(renderOthersItem)}
            </TodoSection>
          )}
          <TodoSection
            title={t("sections.completed")}
            showDividers={bucketShowsDividers("completed")}
            footer={sectionFooter(completed)}
          >
            {merged.completed.map((todo) => renderItem(todo, "completed"))}
          </TodoSection>
        </ContentWrapper>
      </PageLayout>
      <TodoUndoToasts
        completionPendingCount={pendingCount}
        onUndoCompletion={handleUndo}
        revertPendingCount={revertPendingCount}
        onUndoRevert={handleUndoRevert}
        reassignPendingCount={reassignPendingCount}
        onUndoReassignment={handleReassignUndo}
      />
      <TodoEditSheet
        todo={editingTodo}
        isOpen={editingTodo !== null}
        onOpenChange={(open) => {
          if (!open) setEditingTodo(null);
        }}
        onReassign={handleReassign}
      />
    </>
  );
}
